import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const getRandom = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

// ── Google palette ──────────────────────────────────────────────
const G = {
    blue: "#1A73E8",
    blueDark: "#1557B0",
    blueLight: "#E8F0FE",
    blueBorder: "#AECBFA",
    green: "#1E8E3E",
    greenLight: "#E6F4EA",
    greenBorder: "#A8D5B5",
    red: "#D93025",
    redLight: "#FCE8E6",
    redBorder: "#F5C6C3",
    yellow: "#F29900",
    yellowLight: "#FEF7E0",
    yellowBorder: "#FDD663",
    grey50: "#F8F9FA",
    grey100: "#F1F3F4",
    grey200: "#E8EAED",
    grey400: "#BDC1C6",
    grey600: "#80868B",
    grey700: "#5F6368",
    grey900: "#202124",
    white: "#FFFFFF",
};

export default function MentalMathPractice() {
    const navigate = useNavigate();

    const [configInput, setConfigInput] = useState({
        multiplierMin: "12", multiplierMax: "20",
        multiplicandMin: "5", multiplicandMax: "12",
    });
    const [config, setConfig] = useState({
        multiplierMin: 12, multiplierMax: 20,
        multiplicandMin: 5, multiplicandMax: 12,
    });
    const [running, setRunning] = useState(false);
    const [currentQ, setCurrentQ] = useState(null);
    const [answer, setAnswer] = useState("");
    const [feedback, setFeedback] = useState(null); // null | 'correct' | 'wrong'
    const [history, setHistory] = useState([]);
    const [showCorrect, setShowCorrect] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [questionStart, setQuestionStart] = useState(null);

    const inputRef = useRef(null);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const configRef = useRef(config);
    const currentQRef = useRef(currentQ);
    const questionStartRef = useRef(questionStart);
    const showCorrectRef = useRef(showCorrect);
    const historyRef = useRef(history);

    configRef.current = config;
    currentQRef.current = currentQ;
    questionStartRef.current = questionStart;
    showCorrectRef.current = showCorrect;
    historyRef.current = history;

    const generateQuestion = useCallback((cfg) => {
        const a = getRandom(cfg.multiplierMin, cfg.multiplierMax);
        const b = getRandom(cfg.multiplicandMin, cfg.multiplicandMax);
        return { a, b, correct: a * b };
    }, []);

    const startTimer = () => {
        const now = Date.now();
        startTimeRef.current = now;
        setQuestionStart(now);
        setElapsed(0);
        clearInterval(timerRef.current);
        timerRef.current = setInterval(() => {
            setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }, 500);
    };
    const stopTimer = () => clearInterval(timerRef.current);

    const nextQuestion = useCallback((cfg) => {
        const q = generateQuestion(cfg || configRef.current);
        setCurrentQ(q);
        setAnswer("");
        setFeedback(null);
        setShowCorrect(false);
        startTimer();
        setTimeout(() => inputRef.current?.focus(), 50);
    }, [generateQuestion]);

    const handleStart = () => {
        const cfg = {
            multiplierMin: parseInt(configInput.multiplierMin) || 12,
            multiplierMax: parseInt(configInput.multiplierMax) || 20,
            multiplicandMin: parseInt(configInput.multiplicandMin) || 5,
            multiplicandMax: parseInt(configInput.multiplicandMax) || 12,
        };
        setConfig(cfg);
        configRef.current = cfg;
        setHistory([]);
        setRunning(true);
        nextQuestion(cfg);
    };

    const handleStop = () => {
        stopTimer();
        setRunning(false);
        const timeTaken = questionStartRef.current
            ? Math.floor((Date.now() - questionStartRef.current) / 1000) : 0;
        const q = currentQRef.current;
        const entry = {
            question: q ? `${q.a} × ${q.b}` : "",
            submitted: "", timeTaken,
            correctAnswer: q?.correct ?? "",
            hintTaken: showCorrectRef.current,
            result: "skipped",
        };
        const finalHistory = [entry, ...historyRef.current];
        setHistory(finalHistory);
        setCurrentQ(null);
        setAnswer("");
        setFeedback(null);
        setShowCorrect(false);
        // navigate("/results", { state: { history: finalHistory } });
    };

    const commitAnswerWithValue = useCallback((userAns) => {
        const q = currentQRef.current;
        if (!q) return;
        const timeTaken = questionStartRef.current
            ? Math.floor((Date.now() - questionStartRef.current) / 1000) : 0;
        const isCorrect = userAns === q.correct;

        if (isCorrect) {
            setFeedback("correct");
            stopTimer();
            setTimeout(() => nextQuestion(), 500);
        } else {
            const entry = {
                question: `${q.a} × ${q.b}`,
                submitted: userAns, timeTaken,
                correctAnswer: q.correct,
                hintTaken: showCorrectRef.current,
                result: isCorrect ? "correct" : "wrong",
            };
            setHistory(prev => [entry, ...prev]);
            setFeedback("wrong");
        }
    }, [nextQuestion]);

    // Auto-submit when typed number matches correct answer
    useEffect(() => {
        if (!currentQ || !running) return;

        const trimmed = answer.trim();
        const val = parseInt(trimmed);

        if (
            !isNaN(val) &&
            String(val) === trimmed && // ensures it's a clean number (no spaces, chars)
            trimmed.length === String(currentQ.correct).length
        ) {
            commitAnswerWithValue(val);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answer]);

    const commitAnswer = () => {
        if (!currentQ || !answer.trim()) return;
        const val = parseInt(answer.trim());
        if (!isNaN(val)) commitAnswerWithValue(val);
    };

    const handleSkip = () => {
        const q = currentQRef.current;
        if (!q) return;
        const timeTaken = questionStartRef.current
            ? Math.floor((Date.now() - questionStartRef.current) / 1000) : 0;
        const entry = {
            question: `${q.a} × ${q.b}`,
            submitted: "", timeTaken,
            correctAnswer: q.correct,
            hintTaken: showCorrectRef.current,
            result: "skipped",
        };
        setHistory(prev => [entry, ...prev]);
        nextQuestion();
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") { e.preventDefault(); commitAnswer(); }
    };

    useEffect(() => () => clearInterval(timerRef.current), []);

    const fmtTime = (s) => {
        const m = Math.floor(s / 60), sec = s % 60;
        return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
    };

    const correctCount = history.filter(h => h.result === "correct").length;
    const wrongCount = history.filter(h => h.result === "wrong").length;
    const skippedCount = history.filter(h => h.result === "skipped").length;
    const hintCount = history.filter(h => h.hintTaken).length;

    return (
        <div style={{ fontFamily: "Google Sans, Roboto, sans-serif", minHeight: "100vh", background: G.grey50, paddingBottom: "3rem" }}>

            {/* ── TOP CONFIG BAR ── */}
            <div style={{
                background: G.white,
                borderBottom: `1.5px solid ${G.grey200}`,
                padding: "16px 24px",
                display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "20px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            }}>
                <RangePair
                    label="Multiplier"
                    minVal={configInput.multiplierMin} maxVal={configInput.multiplierMax}
                    onMinChange={v => setConfigInput(p => ({ ...p, multiplierMin: v }))}
                    onMaxChange={v => setConfigInput(p => ({ ...p, multiplierMax: v }))}
                    disabled={running}
                />
                <RangePair
                    label="Multiplicand"
                    minVal={configInput.multiplicandMin} maxVal={configInput.multiplicandMax}
                    onMinChange={v => setConfigInput(p => ({ ...p, multiplicandMin: v }))}
                    onMaxChange={v => setConfigInput(p => ({ ...p, multiplicandMax: v }))}
                    disabled={running}
                />
                <div style={{ marginLeft: "auto" }}>
                    {!running ? (
                        <button onClick={handleStart} style={primaryBtn(G.blue, G.blueDark)}>START →</button>
                    ) : (
                        <button onClick={handleStop} style={primaryBtn(G.red, "#b52d22")}>STOP ■</button>
                    )}
                </div>
            </div>

            {/* ── QUESTION AREA ── */}
            {running && currentQ ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "52vh", gap: "22px", padding: "2rem 1rem" }}>

                    {/* Timer pill */}
                    <div style={{ fontSize: "16px", fontWeight: 600, color: G.grey700, background: G.grey100, padding: "7px 20px", borderRadius: "20px", border: `1.5px solid ${G.grey200}` }}>
                        ⏱ {fmtTime(elapsed)}
                    </div>

                    {/* Big question */}
                    <div style={{ fontSize: "clamp(3.5rem, 12vw, 6.5rem)", fontWeight: 700, color: G.grey900, lineHeight: 1, letterSpacing: "-0.02em" }}>
                        {currentQ.a}&nbsp;<span style={{ color: G.grey400, fontWeight: 300 }}>×</span>&nbsp;{currentQ.b}
                    </div>

                    {/* Answer row */}
                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        <input
                            ref={inputRef}
                            type="number"
                            value={answer}
                            onChange={e => { setAnswer(e.target.value); if (feedback === "wrong") setFeedback(null); }}
                            onKeyDown={handleKeyDown}
                            placeholder="?"
                            style={{
                                width: "140px", padding: "14px 16px", fontSize: "1.8rem", textAlign: "center",
                                background: feedback === "wrong" ? G.redLight : feedback === "correct" ? G.greenLight : G.white,
                                border: `2.5px solid ${feedback === "wrong" ? G.red : feedback === "correct" ? G.green : G.grey400}`,
                                borderRadius: "12px", color: G.grey900, fontFamily: "inherit", outline: "none",
                                fontWeight: 700, transition: "border-color 0.15s, background 0.15s",
                            }}
                        />
                        <button onClick={commitAnswer} style={primaryBtn(G.blue, G.blueDark, "14px 24px", "17px")}>
                            OK
                        </button>
                    </div>

                    {feedback === "wrong" && (
                        <div style={{ fontSize: "16px", color: G.red, fontWeight: 600, background: G.redLight, padding: "10px 22px", borderRadius: "10px", border: `1.5px solid ${G.redBorder}` }}>
                            Not quite — try again or skip
                        </div>
                    )}

                    {showCorrect && (
                        <div style={{ fontSize: "18px", color: G.grey700, background: G.yellowLight, borderRadius: "10px", padding: "12px 26px", border: `1.5px solid ${G.yellowBorder}`, fontWeight: 500 }}>
                            Answer: <strong style={{ color: G.grey900, fontWeight: 800 }}>{currentQ.correct}</strong>
                        </div>
                    )}

                    <div style={{ display: "flex", gap: "12px", marginTop: "4px" }}>
                        {!showCorrect && (
                            <button onClick={() => setShowCorrect(true)} style={outlineBtn(G.yellowBorder, G.yellowLight, G.grey900)}>
                                💡 Show Answer
                            </button>
                        )}
                        <button onClick={handleSkip} style={outlineBtn(G.grey400, G.white, G.grey700)}>
                            Skip →
                        </button>
                    </div>
                </div>
            ) : (
                !running && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "30vh", color: G.grey600, fontSize: "18px" }}>
                        {history.length === 0 ? "Configure ranges and press Start" : "Session ended"}
                    </div>
                )
            )}

            {/* ── HISTORY ── */}
            {history.length > 0 && (
                <div style={{ maxWidth: "740px", margin: "0 auto", padding: "0 1.5rem" }}>

                    {/* Summary chips — TOP */}
                    <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap" }}>
                        {[
                            { label: `✓ ${correctCount} correct`, bg: G.greenLight, text: G.green, border: G.greenBorder },
                            { label: `✗ ${wrongCount} wrong`, bg: G.redLight, text: G.red, border: G.redBorder },
                            { label: `– ${skippedCount} skipped`, bg: G.grey100, text: G.grey700, border: G.grey200 },
                            { label: `💡 ${hintCount} hints`, bg: G.yellowLight, text: "#a56800", border: G.yellowBorder },
                        ].map(({ label, bg, text, border }) => (
                            <div key={label} style={{ padding: "8px 18px", borderRadius: "20px", background: bg, border: `1.5px solid ${border}`, fontSize: "15px", color: text, fontWeight: 700 }}>
                                {label}
                            </div>
                        ))}
                    </div>

                    <div style={{ fontSize: "13px", letterSpacing: "0.08em", textTransform: "uppercase", color: G.grey600, marginBottom: "10px", fontWeight: 700 }}>
                        History — {history.length} question{history.length !== 1 ? "s" : ""}
                    </div>

                    <div style={{ border: `1.5px solid ${G.grey200}`, borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 6px rgba(0,0,0,0.07)" }}>
                        {/* Header */}
                        <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 72px 56px 72px", background: G.grey100, borderBottom: `1.5px solid ${G.grey200}`, padding: "11px 18px" }}>
                            {["Question", "Submitted", "Correct", "Time", "Hint", "Result"].map(h => (
                                <div key={h} style={{ fontSize: "13px", letterSpacing: "0.05em", textTransform: "uppercase", color: G.grey700, fontWeight: 700 }}>{h}</div>
                            ))}
                        </div>

                        {history.map((row, i) => {
                            const rowBg = row.result === "correct" ? G.greenLight : row.result === "wrong" ? G.redLight : G.white;
                            const accentClr = row.result === "correct" ? G.green : row.result === "wrong" ? G.red : G.grey400;
                            return (
                                <div key={i} style={{
                                    display: "grid", gridTemplateColumns: "1.3fr 1fr 1fr 72px 56px 72px",
                                    padding: "13px 18px",
                                    borderBottom: i < history.length - 1 ? `1px solid ${G.grey200}` : "none",
                                    background: rowBg,
                                    borderLeft: `4px solid ${accentClr}`,
                                }}>
                                    <HCell bold>{row.question}</HCell>
                                    <HCell muted={row.submitted === ""}>{row.submitted !== "" ? row.submitted : "—"}</HCell>
                                    <HCell>{row.correctAnswer}</HCell>
                                    <HCell muted>{row.timeTaken}s</HCell>
                                    <HCell muted>{row.hintTaken ? "yes" : "no"}</HCell>
                                    <div style={{ fontSize: "16px", fontWeight: 800, color: accentClr }}>
                                        {row.result === "correct" ? "✓" : row.result === "wrong" ? "✗" : "–"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Sub-components ──────────────────────────────────────────────

function RangePair({ label, minVal, maxVal, onMinChange, onMaxChange, disabled }) {
    const inp = {
        width: "72px", padding: "9px 10px", fontSize: "16px", textAlign: "center",
        fontFamily: "Google Sans, Roboto, sans-serif", fontWeight: 600,
        border: `1.5px solid ${disabled ? "#BDC1C6" : "#1A73E8"}`,
        borderRadius: "8px",
        background: disabled ? "#F1F3F4" : "#FFFFFF",
        color: "#202124", outline: "none", transition: "border-color 0.15s",
    };
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            <span style={{ fontSize: "13px", letterSpacing: "0.05em", textTransform: "uppercase", color: "#5F6368", fontWeight: 700 }}>{label}</span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input type="number" value={minVal} onChange={e => onMinChange(e.target.value)} disabled={disabled} style={inp} />
                <span style={{ color: "#80868B", fontSize: "15px", fontWeight: 600 }}>to</span>
                <input type="number" value={maxVal} onChange={e => onMaxChange(e.target.value)} disabled={disabled} style={inp} />
            </div>
        </div>
    );
}

function HCell({ children, muted, bold }) {
    return (
        <div style={{ fontSize: "15px", color: muted ? "#80868B" : "#202124", fontWeight: bold ? 700 : 500, fontFamily: "Google Sans, Roboto, sans-serif" }}>
            {children}
        </div>
    );
}

function primaryBtn(bg, borderColor, padding = "11px 28px", fontSize = "16px") {
    return {
        padding, background: bg, color: "#fff",
        border: `1.5px solid ${borderColor}`,
        borderRadius: "8px", cursor: "pointer",
        fontSize, fontWeight: 700,
        fontFamily: "Google Sans, Roboto, sans-serif",
        boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
        transition: "opacity 0.15s",
    };
}

function outlineBtn(borderColor, bg, color) {
    return {
        padding: "10px 20px", border: `1.5px solid ${borderColor}`,
        borderRadius: "8px", background: bg, cursor: "pointer",
        fontSize: "15px", fontWeight: 600, color,
        fontFamily: "Google Sans, Roboto, sans-serif",
        transition: "background 0.15s",
    };
}